import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ScheduleDay } from '@/types/schedule.types';
import type { PreviewDay } from '@/utils/generateTemplatePreview';

const WEEKDAY_NAMES = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

export interface EmployeeRow {
  id: string;
  first_name: string;
  last_name: string;
  position?: { id: string; name: string } | null;
  schedule: ScheduleDay[];
  total_working_days: number;
  total_days_off: number;
  hasChange?: boolean;
  changeLabel?: string; // e.g. "2/2 дневная" - name of new template
}

interface SchedulePreviewGridProps {
  employees: EmployeeRow[];
  dates: Date[];
  previewSchedules: Map<string, PreviewDay[]>;
  onEmployeeClick?: (employeeId: string) => void;
  onSelectTemplate?: (employeeId: string) => void;
  onRemoveChange?: (employeeId: string) => void;
  selectedEmployeeIds?: Set<string>;
  showDiff?: boolean;
  /** Если указано, preview применяется только с этой даты. Дни до неё — текущий график. */
  effectiveFromDate?: string; // формат YYYY-MM-DD
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getDayColorClass = (scheduleType: string, isWorking: boolean): string => {
  if (!isWorking || scheduleType === 'day_off') {
    return 'bg-gray-100 border-gray-200';
  }
  switch (scheduleType) {
    case 'working':
      return 'bg-green-100 border-green-200';
    case 'night_shift':
      return 'bg-indigo-100 border-indigo-200';
    case 'holiday':
      return 'bg-red-100 border-red-200';
    case 'reduced':
      return 'bg-yellow-100 border-yellow-200';
    default:
      return 'bg-gray-50 border-gray-100';
  }
};

const formatTime = (time: string | null | undefined): string => {
  if (!time) return '';
  const value = time.toString();
  const timePart = value.includes('T') ? value.split('T')[1] : value;
  return timePart.slice(0, 5); // HH:mm
};

const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const toDateStr = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ---------------------------------------------------------------------------
// Summary computation
// ---------------------------------------------------------------------------

interface DailyShiftCounts {
  dayCounts: Map<string, number>;
  nightCounts: Map<string, number>;
}

function computeShiftCounts(
  employees: EmployeeRow[],
  dates: Date[],
  previewSchedules: Map<string, PreviewDay[]>,
  effectiveFromDate?: string,
): DailyShiftCounts {
  const dayCounts = new Map<string, number>();
  const nightCounts = new Map<string, number>();

  for (const date of dates) {
    const dateStr = toDateStr(date);
    const isPast = !!effectiveFromDate && dateStr < effectiveFromDate;
    let dayCount = 0;
    let nightCount = 0;

    for (const emp of employees) {
      const preview = previewSchedules.get(emp.id);
      // Для прошедших дней — всегда текущий график
      if (preview && !isPast) {
        const previewDay = preview.find((p) => p.date === dateStr);
        if (previewDay?.is_working_day) {
          if (previewDay.schedule_type === 'night_shift') {
            nightCount++;
          } else {
            dayCount++;
          }
        }
      } else {
        const scheduleDay = emp.schedule.find((s) => s.date === dateStr);
        if (scheduleDay?.is_working_day) {
          if (scheduleDay.schedule_type === 'night_shift') {
            nightCount++;
          } else {
            dayCount++;
          }
        }
      }
    }

    dayCounts.set(dateStr, dayCount);
    nightCounts.set(dateStr, nightCount);
  }

  return { dayCounts, nightCounts };
}

function computeOriginalShiftCounts(
  employees: EmployeeRow[],
  dates: Date[],
): DailyShiftCounts {
  const dayCounts = new Map<string, number>();
  const nightCounts = new Map<string, number>();

  for (const date of dates) {
    const dateStr = toDateStr(date);
    let dayCount = 0;
    let nightCount = 0;

    for (const emp of employees) {
      const scheduleDay = emp.schedule.find((s) => s.date === dateStr);
      if (scheduleDay?.is_working_day) {
        if (scheduleDay.schedule_type === 'night_shift') {
          nightCount++;
        } else {
          dayCount++;
        }
      }
    }

    dayCounts.set(dateStr, dayCount);
    nightCounts.set(dateStr, nightCount);
  }

  return { dayCounts, nightCounts };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SchedulePreviewGrid: React.FC<SchedulePreviewGridProps> = ({
  employees,
  dates,
  previewSchedules,
  onEmployeeClick,
  onSelectTemplate,
  onRemoveChange,
  selectedEmployeeIds,
  showDiff = false,
  effectiveFromDate,
}) => {
  // Final state: preview where available, current otherwise
  const finalCounts = useMemo(
    () => computeShiftCounts(employees, dates, previewSchedules, effectiveFromDate),
    [employees, dates, previewSchedules, effectiveFromDate],
  );

  // Original state (only needed when showDiff is true)
  const originalCounts = useMemo(
    () => (showDiff ? computeOriginalShiftCounts(employees, dates) : null),
    [employees, dates, showDiff],
  );

  // Resolve the effective schedule for a given employee + date
  const resolveDay = (
    employee: EmployeeRow,
    date: Date,
  ): { day: ScheduleDay | PreviewDay | undefined; isPreview: boolean; isPast: boolean } => {
    const dateStr = toDateStr(date);
    const isPast = !!effectiveFromDate && dateStr < effectiveFromDate;
    const preview = previewSchedules.get(employee.id);
    // Дни до effectiveFromDate — показываем текущий график, не preview
    if (preview && !isPast) {
      return { day: preview.find((p) => p.date === dateStr), isPreview: true, isPast };
    }
    return { day: employee.schedule.find((s) => s.date === dateStr), isPreview: false, isPast };
  };

  // Compute totals for an employee (accounting for preview and effectiveFromDate)
  const getEmployeeTotals = (
    employee: EmployeeRow,
  ): { working: number; off: number } => {
    const preview = previewSchedules.get(employee.id);
    if (preview) {
      if (!effectiveFromDate) {
        let working = 0;
        let off = 0;
        for (const d of preview) {
          if (d.is_working_day) working++;
          else off++;
        }
        return { working, off };
      }
      // С effectiveFromDate: прошедшие дни — из текущего графика, будущие — из preview
      let working = 0;
      let off = 0;
      for (const date of dates) {
        const dateStr = toDateStr(date);
        if (dateStr < effectiveFromDate) {
          const scheduleDay = employee.schedule.find((s) => s.date === dateStr);
          if (scheduleDay?.is_working_day) working++;
          else off++;
        } else {
          const previewDay = preview.find((p) => p.date === dateStr);
          if (previewDay?.is_working_day) working++;
          else off++;
        }
      }
      return { working, off };
    }
    return { working: employee.total_working_days, off: employee.total_days_off };
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderSummaryRow = (
    label: string,
    counts: Map<string, number>,
    bgClass: string,
    labelBgClass: string,
    textColor: string,
    compareCounts?: Map<string, number>,
  ) => (
    <tr className={cn(bgClass, 'border-t border-border')}>
      <td
        className={cn(
          'sticky left-0 z-40 border-r p-2 min-w-[200px] text-left',
          labelBgClass,
        )}
      >
        <span className={cn('font-medium text-sm', textColor)}>{label}</span>
      </td>
      {onSelectTemplate && (
        <td className={cn('sticky left-[200px] z-10 border-r min-w-[140px]', labelBgClass)} />
      )}
      {dates.map((date) => {
        const dateStr = toDateStr(date);
        const count = counts.get(dateStr) ?? 0;
        const isTodayDate = isToday(date);
        const prevCount = compareCounts?.get(dateStr);
        const dropped = prevCount !== undefined && count < prevCount;
        const isZero = count === 0;

        return (
          <td
            key={date.toISOString()}
            className={cn(
              'p-0 border-l text-center',
              isTodayDate && 'bg-blue-50',
              isZero && 'bg-red-100',
            )}
          >
            <div className="h-8 flex items-center justify-center">
              <span
                className={cn(
                  'text-xs font-bold',
                  isZero
                    ? 'text-red-600'
                    : dropped
                      ? 'text-orange-600'
                      : count > 0
                        ? textColor
                        : 'text-gray-400',
                )}
              >
                {count}
              </span>
            </div>
          </td>
        );
      })}
      <td className="sticky right-[50px] z-10 p-1 text-center border-l bg-green-50" />
      <td className="sticky right-0 z-10 p-1 text-center border-l bg-gray-100" />
    </tr>
  );

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full border-collapse">
          {/* ---- Header ---- */}
          <thead className="sticky top-0 z-30">
            <tr className="bg-muted">
              <th className="sticky left-0 z-40 bg-muted border-r min-w-[200px] p-2 text-left font-medium">
                Сотрудник
              </th>
              {onSelectTemplate && (
                <th className="sticky left-[200px] z-40 bg-muted border-r min-w-[140px] p-2 text-left font-medium text-xs">
                  Новый график
                </th>
              )}
              {dates.map((date) => {
                const dayOfWeek = date.getDay();
                const dayNum = date.getDate();
                const isTodayDate = isToday(date);
                const isWeekendDay = isWeekend(date);

                return (
                  <th
                    key={date.toISOString()}
                    className={cn(
                      'min-w-[50px] p-1 text-center text-xs font-medium border-l',
                      isTodayDate && 'bg-blue-100 border-blue-300',
                      isWeekendDay && !isTodayDate && 'bg-gray-100',
                    )}
                  >
                    <div className="flex flex-col items-center">
                      <span
                        className={cn(
                          'text-[10px]',
                          isWeekendDay
                            ? 'text-red-500'
                            : 'text-muted-foreground',
                        )}
                      >
                        {WEEKDAY_NAMES[dayOfWeek]}
                      </span>
                      <span
                        className={cn(
                          'font-bold',
                          isTodayDate && 'text-blue-600',
                          isWeekendDay && !isTodayDate && 'text-red-500',
                        )}
                      >
                        {dayNum}
                      </span>
                    </div>
                  </th>
                );
              })}
              <th className="sticky right-[50px] z-30 min-w-[50px] p-1 text-center text-xs font-medium border-l bg-green-50">
                Раб.
              </th>
              <th className="sticky right-0 z-30 min-w-[50px] p-1 text-center text-xs font-medium border-l bg-gray-100">
                Вых.
              </th>
            </tr>
          </thead>

          {/* ---- Body ---- */}
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td
                  colSpan={dates.length + 3 + (onSelectTemplate ? 1 : 0)}
                  className="p-8 text-center text-muted-foreground"
                >
                  Нет сотрудников
                </td>
              </tr>
            ) : (
              employees.map((employee) => {
                const isSelected = selectedEmployeeIds?.has(employee.id);
                const hasPreview = previewSchedules.has(employee.id);
                const totals = getEmployeeTotals(employee);

                return (
                  <tr
                    key={employee.id}
                    className={cn(
                      'hover:bg-muted/50',
                      isSelected && 'bg-blue-50',
                    )}
                  >
                    {/* Employee name */}
                    <td
                      className={cn(
                        'sticky left-0 z-10 border-r p-2 min-w-[200px]',
                        isSelected ? 'bg-blue-50' : 'bg-background',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => onEmployeeClick?.(employee.id)}
                        className="flex flex-col text-left hover:underline cursor-pointer"
                      >
                        <span
                          className={cn(
                            'font-medium text-sm',
                            isSelected && 'text-blue-700',
                          )}
                        >
                          {employee.hasChange && (
                            <span className="text-blue-500 mr-1" aria-hidden="true">
                              &#9679;
                            </span>
                          )}
                          {employee.last_name} {employee.first_name}
                        </span>
                        {employee.position && (
                          <span className="text-xs text-muted-foreground">
                            {employee.position.name}
                          </span>
                        )}
                        {employee.changeLabel && (
                          <span className="text-[10px] text-blue-600 mt-0.5">
                            {employee.changeLabel}
                          </span>
                        )}
                      </button>
                    </td>

                    {/* New template column */}
                    {onSelectTemplate && (
                      <td
                        className={cn(
                          'sticky left-[200px] z-10 border-r p-1.5 min-w-[140px]',
                          isSelected ? 'bg-blue-50' : 'bg-background',
                        )}
                      >
                        {employee.hasChange ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-blue-700 font-medium truncate flex-1">
                              {employee.changeLabel}
                            </span>
                            {onRemoveChange && (
                              <button
                                type="button"
                                onClick={() => onRemoveChange(employee.id)}
                                className="text-gray-400 hover:text-red-500 text-xs shrink-0"
                                title="Убрать"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onSelectTemplate(employee.id)}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Выбрать...
                          </button>
                        )}
                      </td>
                    )}

                    {/* Schedule cells */}
                    {dates.map((date) => {
                      const { day, isPreview, isPast } = resolveDay(employee, date);
                      const isTodayDate = isToday(date);

                      if (!day) {
                        return (
                          <td
                            key={date.toISOString()}
                            className={cn(
                              'p-0 border-l',
                              isTodayDate && 'ring-2 ring-blue-400 ring-inset',
                            )}
                          >
                            <div className="h-10 bg-gray-50 flex items-center justify-center">
                              <span className="text-[10px] text-gray-400">
                                -
                              </span>
                            </div>
                          </td>
                        );
                      }

                      const colorClass = getDayColorClass(
                        day.schedule_type,
                        day.is_working_day,
                      );

                      const timeDisplay = day.is_working_day
                        ? `${formatTime(day.shift_start)} - ${formatTime(day.shift_end)}`
                        : 'Вых';

                      const tooltipContent = (
                        <div className="text-xs space-y-1">
                          <div className="font-medium">
                            {day.schedule_type === 'night_shift'
                              ? 'Ночная смена'
                              : day.is_working_day
                                ? 'Рабочий день'
                                : 'Выходной'}
                          </div>
                          {day.is_working_day && (
                            <div>
                              {formatTime(day.shift_start)} -{' '}
                              {formatTime(day.shift_end)}
                            </div>
                          )}
                          {isPreview && hasPreview && (
                            <div className="text-blue-600 font-medium">
                              Предпросмотр нового графика
                            </div>
                          )}
                          {isPast && hasPreview && (
                            <div className="text-gray-500 font-medium">
                              Прошедший день — не будет изменён
                            </div>
                          )}
                        </div>
                      );

                      return (
                        <td
                          key={date.toISOString()}
                          className={cn(
                            'p-0 border-l',
                            isTodayDate &&
                              !isPreview &&
                              'ring-2 ring-blue-400 ring-inset',
                          )}
                        >
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div
                                  className={cn(
                                    'h-10 w-full flex flex-col items-center justify-center border transition-colors',
                                    colorClass,
                                    isPreview &&
                                      hasPreview &&
                                      'ring-2 ring-blue-400 ring-inset',
                                    isPast && hasPreview && 'opacity-50',
                                  )}
                                >
                                  {day.is_working_day ? (
                                    <>
                                      <span className="text-[10px] font-medium leading-tight">
                                        {formatTime(day.shift_start)}
                                      </span>
                                      {day.schedule_type === 'night_shift' && (
                                        <span className="text-[8px] text-indigo-600 font-medium">
                                          ноч
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-[10px] text-gray-500">
                                      вых
                                    </span>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-[250px]"
                              >
                                {tooltipContent}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                      );
                    })}

                    {/* Totals */}
                    <td className="sticky right-[50px] z-10 p-1 text-center text-sm font-medium border-l bg-green-50">
                      {totals.working}
                    </td>
                    <td className="sticky right-0 z-10 p-1 text-center text-sm font-medium border-l bg-gray-100">
                      {totals.off}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>

          {/* ---- Footer summary ---- */}
          {employees.length > 0 && (
            <tfoot className="sticky bottom-0 z-20">
              {showDiff && originalCounts ? (
                <>
                  {/* "Было" rows */}
                  {renderSummaryRow(
                    'Было (день)',
                    originalCounts.dayCounts,
                    'bg-muted/60',
                    'bg-muted',
                    'text-gray-500',
                  )}
                  {renderSummaryRow(
                    'Было (ночь)',
                    originalCounts.nightCounts,
                    'bg-indigo-50/40',
                    'bg-indigo-50',
                    'text-gray-500',
                  )}
                  {/* "Стало" rows */}
                  {renderSummaryRow(
                    'Стало (день)',
                    finalCounts.dayCounts,
                    'bg-muted/80',
                    'bg-muted',
                    'text-green-700',
                    originalCounts.dayCounts,
                  )}
                  {renderSummaryRow(
                    'Стало (ночь)',
                    finalCounts.nightCounts,
                    'bg-indigo-50/60',
                    'bg-indigo-50',
                    'text-indigo-700',
                    originalCounts.nightCounts,
                  )}
                </>
              ) : (
                <>
                  {/* Day shifts */}
                  {renderSummaryRow(
                    'На смене (день)',
                    finalCounts.dayCounts,
                    'bg-muted/80 border-t-2',
                    'bg-muted',
                    'text-green-700',
                  )}
                  {/* Night shifts */}
                  {renderSummaryRow(
                    'На смене (ночь)',
                    finalCounts.nightCounts,
                    'bg-indigo-50/60',
                    'bg-indigo-50',
                    'text-indigo-700',
                  )}
                </>
              )}
            </tfoot>
          )}
        </table>
      </div>

      {/* ---- Legend ---- */}
      <div className="border-t p-3 flex flex-wrap gap-4 text-xs bg-muted/30">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-green-100 border border-green-200" />
          <span className="text-muted-foreground">Рабочий день</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-indigo-100 border border-indigo-200" />
          <span className="text-muted-foreground">Ночная смена</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gray-100 border border-gray-200" />
          <span className="text-muted-foreground">Выходной</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-100 border border-red-200" />
          <span className="text-muted-foreground">Праздник</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-yellow-100 border border-yellow-200" />
          <span className="text-muted-foreground">Сокращённый</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded ring-2 ring-blue-400 bg-white" />
          <span className="text-muted-foreground">Предпросмотр изменения</span>
        </div>
      </div>
    </div>
  );
};

export default SchedulePreviewGrid;
